import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { KeyRound, Loader2, Lock, ShieldCheck, X } from 'lucide-react'
import { useAdminSession } from '@hooks/useAdminSession'

function getRemainingTime(expiresAt, now) {
  if (!expiresAt) return null
  const remainingSeconds = Math.max(
    0,
    Math.ceil((new Date(expiresAt).getTime() - now) / 1000)
  )
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export default function AdminModeIndicator() {
  const {
    session,
    elevationPrompt,
    elevate,
    revokeElevation
  } = useAdminSession()
  const [open, setOpen] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [now, setNow] = useState(Date.now())

  const elevation = session?.elevation || {}
  const elevationRequired = Boolean(elevation.requiredForCriticalActions)
  const elevated = Boolean(elevation.active)
  const readOnly = Boolean(session?.environment?.readOnly)

  useEffect(() => {
    if (!elevated) return undefined
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [elevated])

  useEffect(() => {
    if (elevationPrompt > 0 && elevationRequired) {
      setOpen(true)
    }
  }, [elevationPrompt, elevationRequired])

  const remaining = useMemo(
    () => getRemainingTime(elevation.expiresAt, now),
    [elevation.expiresAt, now]
  )

  const close = (force = false) => {
    if (submitting && !force) return
    setOpen(false)
    setTotpCode('')
    setError(null)
  }

  const handleElevate = async event => {
    event.preventDefault()
    if (!/^\d{6}$/.test(totpCode)) return

    try {
      setSubmitting(true)
      setError(null)
      await elevate(totpCode)
      close(true)
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          'Verification failed'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleRevoke = async () => {
    try {
      setSubmitting(true)
      setError(null)
      await revokeElevation()
      close(true)
    } catch (requestError) {
      setError(requestError?.message || 'Failed to leave elevated mode')
    } finally {
      setSubmitting(false)
    }
  }

  let label = 'Admin mode'
  let Icon = ShieldCheck
  let style =
    'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'

  if (readOnly) {
    label = 'Read only'
    Icon = Lock
    style =
      'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
  } else if (elevationRequired && elevated) {
    label = remaining ? `Elevated ${remaining}` : 'Elevated'
    Icon = KeyRound
    style =
      'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
  } else if (elevationRequired) {
    label = 'Standard access'
    Icon = Lock
    style =
      'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
  }

  return (
    <>
      <button
        type="button"
        onClick={() => elevationRequired && setOpen(true)}
        disabled={!elevationRequired || readOnly}
        className={`inline-flex min-h-8 items-center gap-1.5 border px-2 text-xs font-medium disabled:cursor-default ${style}`}
        title={
          elevationRequired
            ? 'Manage recent administrator authentication'
            : 'Critical-action elevation is not required'
        }
      >
        <Icon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span className="hidden whitespace-nowrap sm:inline">{label}</span>
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-mode-title"
          >
            <button
              type="button"
              className="fixed inset-0 bg-black/60"
              onClick={() => close()}
              aria-label="Close admin mode dialog"
            />
            <section className="relative w-full max-w-sm border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2
                    id="admin-mode-title"
                    className="text-lg font-semibold text-gray-950 dark:text-white"
                  >
                    {elevated ? 'Elevated admin mode' : 'Verify admin action'}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {elevated
                      ? `Critical access expires in ${remaining || 'under one minute'}.`
                      : 'Enter the six-digit code from your authenticator.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => close()}
                  disabled={submitting}
                  className="p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              {error && (
                <p
                  className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                  role="alert"
                >
                  {error}
                </p>
              )}

              {elevated ? (
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={submitting}
                  className="mt-5 inline-flex min-h-10 w-full items-center justify-center gap-2 border border-gray-300 px-4 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  {submitting && (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Leave elevated mode
                </button>
              ) : (
                <form onSubmit={handleElevate} className="mt-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Authentication code
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={event =>
                        setTotpCode(
                          event.target.value.replace(/\D/g, '').slice(0, 6)
                        )
                      }
                      className="mt-2 w-full border border-gray-300 bg-white px-3 py-2.5 text-center font-mono text-lg text-gray-950 focus:border-cyan-500 focus:ring-cyan-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      maxLength={6}
                      required
                      autoFocus
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={submitting || !/^\d{6}$/.test(totpCode)}
                    className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 bg-gray-950 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-cyan-500 dark:text-gray-950 dark:hover:bg-cyan-400"
                  >
                    {submitting && (
                      <Loader2
                        className="h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    Verify
                  </button>
                </form>
              )}
            </section>
          </div>,
          document.body
        )}
    </>
  )
}
